import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { pontoService, colaboradorService } from '../../services/api';

const FolhaPonto = () => {
  const { user } = useAuth();
  const [modoMes, setModoMes] = useState('atual'); // atual | passado | especifico
  const [mesEspecifico, setMesEspecifico] = useState(() => new Date().toISOString().slice(0, 7));
  const [colaboradorId, setColaboradorId] = useState('');
  const [colaboradores, setColaboradores] = useState([]);
  const [folha, setFolha] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandido, setExpandido] = useState(null); // id do colaborador expandido
  const [modalDetalhe, setModalDetalhe] = useState(null); // folha do colaborador no modal
  const [mapaModal, setMapaModal] = useState(null); // {lat, lng, label, data}

  const ehAdmin = ['administrador', 'supervisor', 'financeiro', 'coordenador', 'administrativo'].includes(user?.perfil);

  // Calcular período conforme o modo
  const calcularPeriodo = () => {
    const hoje = new Date();
    let ano, mes;
    if (modoMes === 'atual') {
      ano = hoje.getFullYear();
      mes = hoje.getMonth();
    } else if (modoMes === 'passado') {
      const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      ano = d.getFullYear();
      mes = d.getMonth();
    } else {
      const [a, m] = mesEspecifico.split('-').map(Number);
      ano = a;
      mes = m - 1;
    }
    const inicio = new Date(ano, mes, 1);
    const fim = new Date(ano, mes + 1, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return {
      inicio: `${ano}-${pad(mes + 1)}-01`,
      fim: `${ano}-${pad(mes + 1)}-${pad(fim.getDate())}`,
      label: new Date(ano, mes, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    };
  };

  // Carregar colaboradores para admin/supervisor
  useEffect(() => {
    if (ehAdmin) {
      colaboradorService.listar({ limit: 200, ativo: true })
        .then(r => {
          const lista = Array.isArray(r.data?.data?.data) ? r.data.data.data :
                        Array.isArray(r.data?.data) ? r.data.data : [];
          setColaboradores(lista.sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
        })
        .catch(err => console.error('Erro ao carregar colaboradores:', err));
    }
    // eslint-disable-next-line
  }, []);

  const formatHora = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
  };

  const formatMin = (valor) => valor || '00:00';

  const badgeSaldo = (status, valor) => {
    const cls = status === 'positivo' ? 'bg-green-100 text-green-800 border-green-300' :
               status === 'negativo' ? 'bg-red-100 text-red-800 border-red-300' :
               'bg-gray-100 text-gray-800 border-gray-300';
    const icon = status === 'positivo' ? '🟢' : status === 'negativo' ? '🔴' : '⚪';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
        <span className="mr-1">{icon}</span>
        {valor}
      </span>
    );
  };

  const buscarFolha = async () => {
    const periodo = calcularPeriodo();
    setLoading(true);
    try {
      const response = await pontoService.folha(periodo.inicio, periodo.fim, colaboradorId);
      if (response.data?.success) {
        setFolha(response.data.folha || []);
        setExpandido(null);
        setModalDetalhe(null);
        if (response.data.folha?.length === 0) {
          toast(`Nenhum ponto encontrado em ${periodo.label}`, { icon: 'ℹ️' });
        }
      } else {
        toast.error(response.data?.message || 'Erro ao gerar folha');
      }
    } catch (error) {
      console.error('Erro ao buscar folha:', error);
      const msg = error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'Erro ao buscar folha';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscarFolha();
    // eslint-disable-next-line
  }, [modoMes, mesEspecifico, colaboradorId]);

  const abrirDetalhe = (f) => {
    setModalDetalhe(f);
  };

  const baixarPdf = async (f) => {
    const periodo = calcularPeriodo();
    try {
      toast.loading('Gerando PDF...');
      const response = await pontoService.folhaPdf(periodo.inicio, periodo.fim, f.colaborador._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `folha_ponto_${f.colaborador.nome.replace(/\s+/g, '_')}_${periodo.label.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.dismiss();
      toast.error('Erro ao gerar PDF');
    }
  };

  const abrirMapa = (lat, lng, label) => {
    if (!lat || !lng) {
      toast('Localização não disponível', { icon: '📍' });
      return;
    }
    setMapaModal({ lat, lng, label });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Horas e saldos</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Folha de Ponto</h1>
            <p className="text-indigo-200 mt-1 text-sm">Acompanhe horas trabalhadas e saldo de todos os funcionários</p>
          </div>

          {/* Controles de período */}
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-indigo-300 mb-1">Período</label>
              <select
                value={modoMes}
                onChange={(e) => setModoMes(e.target.value)}
                className="px-3 py-2 border border-white/20 bg-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-white/50 [color-scheme:dark]"
              >
                <option value="atual">Mês atual</option>
                <option value="passado">Mês passado</option>
                <option value="especifico">Mês específico</option>
              </select>
            </div>
            {modoMes === 'especifico' && (
              <div>
                <label className="block text-xs font-medium text-indigo-300 mb-1">Escolher mês</label>
                <input
                  type="month"
                  value={mesEspecifico}
                  onChange={(e) => setMesEspecifico(e.target.value)}
                  className="px-3 py-2 border border-white/20 bg-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-white/50 [color-scheme:dark]"
                />
              </div>
            )}
            {ehAdmin && (
              <div>
                <label className="block text-xs font-medium text-indigo-300 mb-1">Funcionário</label>
                <select
                  value={colaboradorId}
                  onChange={(e) => setColaboradorId(e.target.value)}
                  className="px-3 py-2 border border-white/20 bg-white/10 text-white rounded-xl text-sm min-w-[180px]"
                >
                  <option value="">Todos</option>
                  {colaboradores.map(c => (
                    <option key={c._id} value={c._id}>{c.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={buscarFolha}
              disabled={loading}
              className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-medium px-5 py-2.5 rounded-xl text-sm disabled:opacity-40 transition"
            >
              {loading ? 'Gerando...' : 'Gerar Folha'}
            </button>
          </div>
        </div>
      </div>

      {/* Resumo geral */}
      {folha.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
            <p className="text-xs text-gray-500">Funcionários</p>
            <p className="text-2xl font-bold text-gray-900">{folha.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
            <p className="text-xs text-gray-500">🟢 Positivas</p>
            <p className="text-2xl font-bold text-green-600">
              {folha.filter(f => f.resumo.statusFinal === 'positivo').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
            <p className="text-xs text-gray-500">🔴 Negativas</p>
            <p className="text-2xl font-bold text-red-600">
              {folha.filter(f => f.resumo.statusFinal === 'negativo').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-gray-400">
            <p className="text-xs text-gray-500">⚪ Zeradas</p>
            <p className="text-2xl font-bold text-gray-700">
              {folha.filter(f => f.resumo.statusFinal === 'zerado').length}
            </p>
          </div>
        </div>
      )}

      {/* Grade de cards - 3 colunas */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          <p className="mt-3 text-gray-500">Gerando folha de ponto...</p>
        </div>
      ) : folha.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-4xl mb-3">📄</div>
          <p className="text-gray-500">Nenhum ponto encontrado no período</p>
          <p className="text-sm text-gray-400 mt-1">Tente outro mês ou verifique se há batidas registradas</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {folha.map((f) => {
            const statusFinal = f.resumo.statusFinal;
            const borderCls = statusFinal === 'positivo' ? 'border-green-500' :
                              statusFinal === 'negativo' ? 'border-red-500' : 'border-gray-400';
            const bgCls = statusFinal === 'positivo' ? 'from-green-50 to-white' :
                          statusFinal === 'negativo' ? 'from-red-50 to-white' : 'from-gray-50 to-white';
            const statusTexto = statusFinal === 'positivo' ? 'Horas positivas 🟢' :
                                statusFinal === 'negativo' ? 'Horas negativas 🔴' : 'Horas zeradas ⚪';

            return (
              <div key={f.colaborador._id} className={`bg-gradient-to-br ${bgCls} rounded-xl shadow-sm border-t-4 ${borderCls} overflow-hidden flex flex-col`}>
                {/* Topo do card */}
                <div className="p-4 flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {f.colaborador.nome?.split(' ').map(p => p[0]).slice(0, 2).join('')}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{f.colaborador.nome}</h3>
                      <p className="text-xs text-gray-500">{f.colaborador.matricula} · {f.colaborador.perfil}</p>
                    </div>
                  </div>

                  {/* Saldo principal */}
                  <div className="text-center mb-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo do mês</p>
                    <p className={`text-3xl font-bold ${statusFinal === 'positivo' ? 'text-green-600' : statusFinal === 'negativo' ? 'text-red-600' : 'text-gray-600'}`}>
                      {f.resumo.saldoFinal}
                    </p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                      statusFinal === 'positivo' ? 'bg-green-100 text-green-800 border-green-300' :
                      statusFinal === 'negativo' ? 'bg-red-100 text-red-800 border-red-300' :
                      'bg-gray-100 text-gray-800 border-gray-300'
                    }`}>
                      {statusTexto}
                    </span>
                  </div>

                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-2 text-center mb-3">
                    <div className="bg-white/70 rounded-lg p-2">
                      <p className="text-[10px] text-gray-500 uppercase">Trabalhado</p>
                      <p className="text-sm font-semibold text-gray-800">{f.resumo.totalTrabalhado}</p>
                    </div>
                    <div className="bg-white/70 rounded-lg p-2">
                      <p className="text-[10px] text-gray-500 uppercase">Previsto</p>
                      <p className="text-sm font-semibold text-gray-800">{f.resumo.totalPrevisto}</p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    Carga {String(f.cargaHoraria).replace('.', ',')}h/dia · {f.resumo.diasComputados} dias
                  </p>
                </div>

                {/* Botão detalhes */}
                <div className="border-t border-gray-200 bg-white/60 p-3">
                  <button
                    onClick={() => abrirDetalhe(f)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition"
                  >
                    📂 Abrir detalhamento
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de detalhamento */}
      {modalDetalhe && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
             onClick={() => setModalDetalhe(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8"
               onClick={(e) => e.stopPropagation()}>
            {/* Cabeçalho do modal */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{modalDetalhe.colaborador.nome}</h3>
                <p className="text-sm text-gray-500">
                  {modalDetalhe.colaborador.matricula} · {modalDetalhe.colaborador.perfil} · Carga {String(modalDetalhe.cargaHoraria).replace('.', ',')}h/dia
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => baixarPdf(modalDetalhe)}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                >
                  🖨️ Gerar PDF
                </button>
                <button
                  onClick={() => setModalDetalhe(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >×</button>
              </div>
            </div>

            {/* Resumo no modal */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-4">
              <div className="flex-1 min-w-[120px]">
                <p className="text-xs text-gray-500">Horas trabalhadas</p>
                <p className="text-lg font-bold text-gray-800">{modalDetalhe.resumo.totalTrabalhado}</p>
              </div>
              <div className="flex-1 min-w-[120px]">
                <p className="text-xs text-gray-500">Horas previstas</p>
                <p className="text-lg font-bold text-gray-800">{modalDetalhe.resumo.totalPrevisto}</p>
              </div>
              <div className="flex-1 min-w-[120px]">
                <p className="text-xs text-gray-500">Saldo final</p>
                <p className={`text-lg font-bold ${
                  modalDetalhe.resumo.statusFinal === 'positivo' ? 'text-green-600' :
                  modalDetalhe.resumo.statusFinal === 'negativo' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {modalDetalhe.resumo.saldoFinal}
                </p>
              </div>
            </div>

            {/* Tabela de dias - Desktop */}
            <div className="hidden md:block overflow-auto max-h-[55vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <th className="px-4 py-3 text-left font-medium">Data</th>
                    <th className="px-4 py-3 text-center font-medium">Entrada</th>
                    <th className="px-4 py-3 text-center font-medium">Saída Int.</th>
                    <th className="px-4 py-3 text-center font-medium">Retorno</th>
                    <th className="px-4 py-3 text-center font-medium">Saída</th>
                    <th className="px-4 py-3 text-center font-medium">Trabalhado</th>
                    <th className="px-4 py-3 text-center font-medium">Previsto</th>
                    <th className="px-4 py-3 text-center font-medium">Saldo Dia</th>
                    <th className="px-4 py-3 text-center font-medium">Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {modalDetalhe.dias.map((dia, idx) => (
                    <tr key={idx} className="border-t border-gray-100 hover:bg-blue-50/50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{dia.dataFormatada}</td>
                      <td className="px-4 py-2.5 text-center">
                        {dia.localizacao?.entrada?.latitude ? (
                          <button onClick={() => abrirMapa(dia.localizacao.entrada.latitude, dia.localizacao.entrada.longitude, 'Entrada')}
                            className="text-blue-600 hover:text-blue-800 underline font-medium">
                            {formatHora(dia.entrada)}
                          </button>
                        ) : formatHora(dia.entrada)}
                      </td>
                      <td className="px-4 py-2.5 text-center">{formatHora(dia.saidaIntervalo)}</td>
                      <td className="px-4 py-2.5 text-center">{formatHora(dia.retorno)}</td>
                      <td className="px-4 py-2.5 text-center">
                        {dia.localizacao?.saida?.latitude ? (
                          <button onClick={() => abrirMapa(dia.localizacao.saida.latitude, dia.localizacao.saida.longitude, 'Saída')}
                            className="text-blue-600 hover:text-blue-800 underline font-medium">
                            {formatHora(dia.saida)}
                          </button>
                        ) : formatHora(dia.saida)}
                      </td>
                      <td className="px-4 py-2.5 text-center font-medium">{dia.horasTrabalhadas}</td>
                      <td className="px-4 py-2.5 text-center text-gray-500">{dia.cargaPrevista}</td>
                      <td className="px-4 py-2.5 text-center">{badgeSaldo(dia.statusSaldo, dia.saldoDia)}</td>
                      <td className="px-4 py-2.5 text-center">{badgeSaldo(dia.statusAcumulado, dia.saldoAcumulado)}</td>
                    </tr>
                  ))}
                  {modalDetalhe.dias.length === 0 && (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center text-gray-400">Sem registros no período</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Cards de dias - Mobile */}
            <div className="md:hidden max-h-[55vh] overflow-y-auto">
              {modalDetalhe.dias.map((dia, idx) => (
                <div key={idx} className="border-b border-gray-100 p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">{dia.dataFormatada}</span>
                    {badgeSaldo(dia.statusSaldo, dia.saldoDia)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-[10px] text-gray-500 uppercase">Entrada</p>
                      {dia.localizacao?.entrada?.latitude ? (
                        <button onClick={() => abrirMapa(dia.localizacao.entrada.latitude, dia.localizacao.entrada.longitude, 'Entrada')}
                          className="font-medium text-blue-600 hover:text-blue-800 underline">
                          {formatHora(dia.entrada)}
                        </button>
                      ) : <p className="font-medium">{formatHora(dia.entrada)}</p>}
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-[10px] text-gray-500 uppercase">Saída</p>
                      {dia.localizacao?.saida?.latitude ? (
                        <button onClick={() => abrirMapa(dia.localizacao.saida.latitude, dia.localizacao.saida.longitude, 'Saída')}
                          className="font-medium text-blue-600 hover:text-blue-800 underline">
                          {formatHora(dia.saida)}
                        </button>
                      ) : <p className="font-medium">{formatHora(dia.saida)}</p>}
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-[10px] text-gray-500 uppercase">Trabalhado</p>
                      <p className="font-medium">{dia.horasTrabalhadas}</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-[10px] text-gray-500 uppercase">Previsto</p>
                      <p className="font-medium text-gray-600">{dia.cargaPrevista}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Acumulado: {badgeSaldo(dia.statusAcumulado, dia.saldoAcumulado)}</span>
                  </div>
                </div>
              ))}
              {modalDetalhe.dias.length === 0 && (
                <div className="p-6 text-center text-gray-400">Sem registros no período</div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal do Mapa */}
      {mapaModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
             onClick={() => setMapaModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-blue-600">
              <div className="text-white">
                <h3 className="font-bold">📍 {mapaModal.label}</h3>
                <p className="text-xs text-blue-100">
                  {mapaModal.lat.toFixed(5)}, {mapaModal.lng.toFixed(5)}
                </p>
              </div>
              <button
                onClick={() => setMapaModal(null)}
                className="text-white hover:text-gray-200 text-2xl font-bold"
              >×</button>
            </div>
            <div className="h-80 w-full">
              <iframe
                title="Mapa da localização"
                src={`https://www.google.com/maps?q=${mapaModal.lat},${mapaModal.lng}&z=17&output=embed`}
                className="w-full h-full border-0"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="px-5 py-3 flex items-center justify-between bg-gray-50">
              <p className="text-sm text-gray-600">{mapaModal.label} da batida de ponto</p>
              <a
                href={`https://www.google.com/maps?q=${mapaModal.lat},${mapaModal.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Abrir no Google Maps ↗
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolhaPonto;
